import { useState } from 'react';
import { FORCE_RULES, QUARTER } from '../shared/q3';
import type { ForceStanding, ForceSummary } from './standings';
import { ArenaNav, MeterRow } from './ArenaView';
import { COH, ForceNearCard, RoleTag, StatusTag, nearEmptyNote, nearestForce } from './parts';
import { count, pc } from './format';

type Filter = 'all' | 'q' | 'r';

const FILTERS: Array<{ f: Filter; label: string; test: (r: ForceStanding) => boolean }> = [
  { f: 'all', label: 'All', test: () => true },
  { f: 'q', label: 'Qualified', test: (r) => r.qualified },
  { f: 'r', label: 'In the race', test: (r) => !r.qualified },
];

interface Props {
  force: ForceStanding[];
  summary: ForceSummary[];
  meId: string | null;
  /** Someone looked up on My Progress; their row is highlighted. */
  focusId: string | null;
  pace: number;
}

export default function ForceArenaView({ force, summary, meId, focusId, pace }: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const C = COH['Star Force'];
  const of = (g: ForceSummary['group']) => summary.find((s) => s.group === g)!;
  const all = of('All');
  const wa = of('Wealth Acquisition');
  const ic = of('IC');
  const q = search.trim().toLowerCase();
  const test = FILTERS.find((x) => x.f === filter)!.test;
  const list = force.filter((r) => test(r) && (!q || (r.rm + ' ' + r.role + ' ' + r.loc).toLowerCase().includes(q)));
  const near = nearestForce(force);
  // Only people with business make the podium; ties share a medal number.
  const top = force.filter((r) => r.ranked).slice(0, 3);
  const podium = [top[1], top[0], top[2]];

  return (
    <div className="sk-view sk-arena" id="arena">
      <ArenaNav
        cohort="Star Force"
        search={search}
        placeholder="Search name, role or city"
        onSearch={setSearch}
      />

      <div className="sk-banner2" id="arena-banner">
        <div className={'sk-emb ' + C.em} />
        <div className="sk-arena-h">
          <div className="sk-eyebrow">{QUARTER.label} leaderboard</div>
          <h2 className="sk-goldtext">{C.name}</h2>
          <div className="crit">{C.crit}</div>
          <div className="sk-kpis">
            {[
              { label: 'Qualified', g: all, sub: 'IC and Wealth Acquisition' },
              { label: 'Wealth Acquisition', g: wa, sub: `qualified at ${FORCE_RULES.qualifyAt * 100}% achieved` },
              { label: 'IC', g: ic, sub: `qualified at ${FORCE_RULES.qualifyAt * 100}% achieved` },
            ].map(({ label, g, sub }) => (
              <div className="sk-kpi" key={label}>
                <div className="l">{label}</div>
                <div className="v">
                  <span>{count(g.qualified)}</span>
                  <span style={{ color: 'var(--sk-faint)', fontWeight: 400 }}> / </span>
                  <span>{count(g.members)}</span>
                </div>
                <div className="s">{sub}</div>
              </div>
            ))}
            <div className="sk-kpi">
              <div className="l">Average achieved</div>
              <div className="v">{pc(all.avgAchieved)}</div>
              <div className="s">across Star Force</div>
            </div>
          </div>
        </div>
      </div>

      {/* No podium until someone has business on the board. */}
      {top.length > 0 && (
        <section className="sk-sec" id="podium">
          <div className="sk-sec-h">
            <div>
              <div className="sk-eyebrow">{C.name}</div>
              <h2>The podium</h2>
            </div>
            <p className="sk-note">Qualified first, then by achieved %</p>
          </div>
          <div className="sk-podium">
            {podium.map((r, i) => {
              if (!r) return null;
              const place = [2, 1, 3][i];
              return (
                <div key={r.empId} className={'sk-pod p' + place}>
                  {place === 1 && <div className="sk-crown">👑</div>}
                  <div className="sk-medal">{r.rank}</div>
                  <div className="nm">{r.rm}</div>
                  <div className="meta">{r.role + ' · ' + r.loc}</div>
                  <div className={'amt' + (place === 1 ? ' sk-goldtext' : '')}>{pc(r.achieved)}</div>
                  <div className="pct">{r.qualified ? 'achieved · qualified' : 'achieved · ' + r.needLabel}</div>
                  <div className="tag">
                    <StatusTag r={r} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="sk-sec" id="leaderboard">
        <div className="sk-sec-h">
          <div>
            <div className="sk-eyebrow">{C.name}</div>
            <h2>Leaderboard</h2>
          </div>
          <div className="sk-tools">
            <div className="sk-seg" role="group" aria-label="Show">
              {FILTERS.map((x) => (
                <button key={x.f} aria-pressed={filter === x.f} onClick={() => setFilter(x.f)}>
                  {x.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="sk-board sk-fboard">
          <table className="sk-tbl sk-ftbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Role</th>
                <th className="hide-m hide-l">City</th>
                <th>Achieved</th>
                <th>Progress</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const isMe = r.empId === meId;
                const cls = [r.qualified && 'q', r.ranked && r.rank <= 3 && 'r' + r.rank, q && 'hl', isMe && 'me', r.empId === focusId && 'focus']
                  .filter(Boolean)
                  .join(' ');
                return (
                  <tr key={r.empId} className={cls} data-emp={r.empId}>
                    <td>
                      <span className="sk-rank">{r.ranked ? r.rank : '–'}</span>
                    </td>
                    <td className="sk-rm" title={r.rm}>
                      {r.rm}
                      {isMe && <span className="sk-you">YOU</span>}
                    </td>
                    <td>
                      <RoleTag role={r.role} />
                    </td>
                    <td className="hide-m hide-l sk-trunc" title={r.loc}>
                      {r.loc}
                    </td>
                    <td className="sk-nowrap">{pc(r.achieved)}</td>
                    <td>
                      <div className="sk-meter">
                        <MeterRow
                          cls="t"
                          label="To 50%"
                          value={r.pct}
                          text={pc(r.achieved)}
                          done={r.qualified}
                          pace={pace}
                        />
                      </div>
                    </td>
                    <td>
                      <StatusTag r={r} />
                    </td>
                  </tr>
                );
              })}
              {!list.length && (
                <tr>
                  <td className="sk-empty" colSpan={7}>
                    No one matches.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="sk-note" style={{ marginTop: 10 }}>
          Achieved % comes straight from the MIS. {FORCE_RULES.qualifyAt * 100}% or more qualifies; the bar fills at
          the {FORCE_RULES.qualifyAt * 100}% line and the white tick is where today's pace says you should be.
        </p>
      </section>

      <section className="sk-sec" id="striking-distance">
        <div className="sk-sec-h">
          <div>
            <div className="sk-eyebrow">{C.name}</div>
            <h2>Almost there</h2>
          </div>
          <p className="sk-note">Not yet qualified, closest to the line</p>
        </div>
        <div className="sk-strike">
          {near.map((r) => (
            <ForceNearCard key={r.empId} r={r} />
          ))}
          {!near.length && <p className="sk-note">{nearEmptyNote(force, 'Everyone in Star Force has qualified!')}</p>}
        </div>
      </section>
    </div>
  );
}
